import { useRef, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useClickOutside } from '../../lib/useClickOutside';
import { CHART_METRICS, DEFAULT_CHART_METRIC_KEYS } from './trendSeries';

// The chart selector: a checklist of which metric cards appear (drawn from the
// chart catalogue, CHART_METRICS). Mirrors the explorer's column selector.
// Toggling persists via the caller (folds into the reporting-charts
// gridPreferences). At least one card always stays on.
export function ReportingChartsMenu({
  selected,
  onChange,
  multiTrend,
  onMultiTrendChange,
}: {
  selected: readonly string[];
  onChange: (next: string[]) => void;
  /** Whether the multi-metric trend chart is on the canvas. */
  multiTrend: boolean;
  onMultiTrendChange: (next: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside([rootRef], () => setOpen(false), open);

  const set = new Set(selected);
  const toggle = (key: string) => {
    const next = new Set(set);
    if (next.has(key)) {
      if (next.size === 1) return; // keep at least one card
      next.delete(key);
    } else {
      next.add(key);
    }
    // Preserve catalogue order.
    onChange(CHART_METRICS.filter((m) => next.has(m.key)).map((m) => m.key));
  };

  const reset = () => onChange([...DEFAULT_CHART_METRIC_KEYS]);

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2 py-1.5 text-sm hover:bg-slate-50"
        title="Choose which metric charts to show"
      >
        <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" aria-hidden />
        <span className="text-xs text-slate-500">Charts</span>
        <span className="font-medium">{selected.length}</span>
        <span className="text-slate-400" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 max-h-[70vh] w-60 overflow-y-auto rounded-md border border-slate-300 bg-white p-2 text-sm shadow-lg"
        >
          {/* The multi-metric trend — a CHART, not a metric card, so it gets its
              own section rather than sitting in the list of cards below. Same
              chart the Google dashboard carries; here it is the one optional
              chart, which is why this is a tick rather than a catalogue. */}
          <div className="mb-1 px-1 text-xs font-medium text-slate-500">Charts</div>
          <label className="mb-2 flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-slate-50">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={multiTrend}
              onChange={(e) => onMultiTrendChange(e.target.checked)}
            />
            <span>
              <span className="block">Trend (multi-metric)</span>
              <span className="block text-xs text-slate-500">
                Tick several metrics onto one chart — spend, leads and ctr together
              </span>
            </span>
          </label>

          <div className="mb-1 flex items-center justify-between border-t border-slate-200 px-1 pt-2">
            <span className="text-xs font-medium text-slate-500">Metric cards</span>
            <button type="button" onClick={reset} className="text-xs text-blue-600 hover:underline">
              Reset to default
            </button>
          </div>
          <div className="space-y-0.5">
            {CHART_METRICS.map((m) => {
              const checked = set.has(m.key);
              const lastOne = checked && set.size === 1;
              return (
                <label
                  key={m.key}
                  className={`flex items-center gap-2 rounded px-1 py-1 ${lastOne ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}
                  title={lastOne ? 'At least one chart stays visible' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={lastOne}
                    onChange={() => toggle(m.key)}
                  />
                  <span className="truncate lowercase">{m.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
